import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  FormGenerator,
  applyFormErrorsRecursively,
} from '@gsa/afp-shared-form-utils';
import { Spinner, Alert, Button } from '@gsa/afp-component-library';
import { useNavigate } from 'react-router-dom';
import {
  useGetCountries,
  useGetStates,
  useCreateAdditionalrequest,
} from '../../../../../requests/additionals-request.jsx';
import {
  changeGarageAddress,
  changeProjectedTowingWeight,
  changeAdditionalsStep,
  setAdditionalsRequestStatus,
  setUsedForTowing,
  setSelectedReason,
  setVehicleAcknowledged,
  setSubmitted,
  setAdditionalRequestId,
  setSelectedCustomerAccount,
  setAdditionalsDraftData,
  setLastSin,
  setSelectedAdditionalOptions,
  setDeliquencyCheck,
} from '../../../../../reducers/additionals-reducer.jsx';
import generateAdditionalsGarageFormContent from './additionals-garage-form-content.jsx';
import { useParams } from 'react-router-dom';
import { additionalsGarageSchema } from './additionals-garage-form-schema.jsx';
import AvailableVehiclesAdditionals from './available-vehicles/available-vehicles.jsx';
import AdditionalRequestActionButtons from '../../../components/additionals-request-buttons/additionals-request-buttons.jsx';
import { ADDITIONAL_VEHICLE_STEPS } from '../../create-vehicle-request-steps.jsx';
import useCreateAdditionalDraft from '../../../components/create-draft/use-create-additional-draft.js';
import { useGetStateList } from '../../../../../requests/additionals-request.jsx';
import { useCurrentUser } from '@gsa/afp-shared-ui-utils';
import { UserRoles, UserTypes } from '../../../../../constants';
import {
  ADDITIONAL_REQUEST_STATUS,
  isExistInRoles,
  validateAdditionalsButtons,
} from '../../../utils/additional-utils';
import useBackToList from '../../../utils/use-back-to-list';

export default function AdditionalsVehicleGarage() {
  const dispatch = useDispatch();
  const {
    garageAddress,
    projectedTowingWeight,
    lastSin,
    usedForTowing,
    selectedStandardItem,
    selectedReasonForRequest,
    selectedCustomerAccount,
    isAcknowledged,
    additionalsDraftData,
  } = useSelector((state) => state.additionalsReducer);

  const formGeneratorRef = useRef();
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const { requestId } = useParams();
  const { backToList } = useBackToList();

  const countries = useGetCountries();
  const defaultStates = useGetStates(garageAddress?.country || 'US', {
    skip: requestId && !garageAddress,
  });
  const [sinAlert, setSinAlert] = useState(false);
  const [createAdditionalrequest, { loading: creatingAdditionalrequest }] =
    useCreateAdditionalrequest();
  const [createDraft] = useCreateAdditionalDraft();

  const roles = currentUser?.roles || [];
  const isEmployee = currentUser?.userType.id === UserTypes.GSA_EMPLOYEE;
  // Internal/GSA users
  const isOrderingAdmin = isExistInRoles(roles, UserRoles.ORDERING_ADMIN);
  const isFsr = isExistInRoles(roles, UserRoles.FSR);
  const isSiteAdmin = isExistInRoles(roles, UserRoles.SITE_ADMIN);
  const reOpenUsers = isOrderingAdmin || isFsr || isSiteAdmin;

  // Customer type users
  const isCustomerUser =
    isExistInRoles(roles, UserRoles.CUSTOMER_ADMINISTRATOR) ||
    isExistInRoles(roles, UserRoles.CUSTOMER_FLEET_MANAGER);
  const agencyLevelApprover =
    isCustomerUser && isExistInRoles(roles, UserRoles.AGENCY_APPROVER);
  const bureauLevelApprover =
    isCustomerUser && isExistInRoles(roles, UserRoles.BUREAU_APPROVER);
  const designatedLevelUsers = agencyLevelApprover || bureauLevelApprover;
  const editUsers = isCustomerUser || isOrderingAdmin || isSiteAdmin;

  const loading = [countries.loading, defaultStates.loading].some(
    (loading) => loading,
  );
  const { content, values } = generateAdditionalsGarageFormContent({
    defaultValues: {
      provideReason: selectedReasonForRequest,
      acknowledge: isAcknowledged,
      countryCode: garageAddress?.country || 'US',
      addressLine1: garageAddress?.address1,
      addressLine2: garageAddress?.address2,
      city: garageAddress?.city,
      stateCode: garageAddress?.state || '-1',
      zipCode: garageAddress?.zip,
      usedForTowing: usedForTowing ? 'yes' : 'no',
      towingWeight: projectedTowingWeight?.value || null,
      towingWeightUnit: projectedTowingWeight?.unit || 'lbs',
    },
    countries,
    defaultStates,
    sinAlert,
  });

  const handleSubmit = (data, _, draft) => {
    dispatch(setSelectedReason(data?.provideReason));
    const towingWeight = data.usedForTowing === 'yes';

    let payload = {
      acknowledged: true,
      additionalRequestId: requestId,
      agencyCode: selectedCustomerAccount?.agencyCode,
      bureauCode: selectedCustomerAccount?.bureauCode,
      officeCode: selectedCustomerAccount?.officeCode,
      customerId: selectedCustomerAccount?.customerAccountId,
      garageAddress: {
        countryCode: data?.countryCode,
        addressLine1: data?.addressLine1,
        addressLine2: data?.addressLine2,
        city: data?.city,
        stateCode: data?.stateCode,
        zipcode: data?.zipCode,
      },
      requestStatusCode: 'DRAFT',
      requestType: data.provideReason,
      sin: `${selectedStandardItem?.standardItemNumber}`,
      specificInstructions: null,
      towingWeight: data.towingWeight || projectedTowingWeight?.value,
      options: [],
    };

    dispatch(setUsedForTowing(data.usedForTowing === 'yes'));
    dispatch(setVehicleAcknowledged(data.acknowledge));
    dispatch(
      changeGarageAddress({
        address1: data.addressLine1,
        address2: data.addressLine2,
        city: data.city,
        country: data.countryCode,
        state: data.stateCode,
        zip: data.zipCode,
      }),
    );

    dispatch(
      changeProjectedTowingWeight(
        data.usedForTowing === 'no'
          ? null
          : {
              value: data.towingWeight,
              unit: data.towingWeightUnit,
            },
      ),
    );

    if (selectedStandardItem?.standardItemNumber !== lastSin) {
      dispatch(setLastSin(selectedStandardItem?.standardItemNumber));
      dispatch(setSelectedAdditionalOptions([]));
      payload.options = [];
    }

    if (towingWeight) {
      payload.towingWeight = `${data.towingWeight} ${data.towingWeightUnit}`;
    } else {
      payload.towingWeight = '';
    }

    if (selectedStandardItem?.standardItemNumber) {
      setSinAlert(false);
      createDraft(payload)
        .then(({ data }) => {
          if (data?.additionalRequestId) {
            if (!draft) {
              const target = `/lease/additionals/request/${data?.additionalRequestId}`;
              dispatch(setAdditionalRequestId(data.additionalRequestId));
              dispatch(setSelectedCustomerAccount(data.customerAccount));
              dispatch(setAdditionalsDraftData(data));
              navigate(target, { replace: true });
              dispatch(setAdditionalsRequestStatus(data?.requestStatusCode));
              dispatch(setDeliquencyCheck(data?.deliquencyCheck));
              dispatch(
                changeAdditionalsStep(ADDITIONAL_VEHICLE_STEPS.SELECT_OPTIONS),
              );
              window.scrollTo(0, 0);
            }
          }
        })
        .catch(console.error);
    } else {
      setSinAlert(true);
      return;
    }
  };

  const handleSaveDraft = async () => {
    const { updateButtonControls } = formGeneratorRef.current;
    const {
      // formState: { errors },
      setError,
      clearErrors,
    } = formGeneratorRef.current;
    const values = formGeneratorRef.current.getValues();
    const { errors } = await formGeneratorRef.current.validate(
      values,
      {},
      {},
      true,
    );

    if (Object.keys(errors).length > 0) {
      for (const key of Object.keys(errors)) {
        applyFormErrorsRecursively(key, errors, setError, clearErrors);
      }
      window.scrollTo(0, 0);
      return;
    }

    if (!selectedStandardItem?.standardItemNumber) {
      setSinAlert(true);
      return;
    } else {
      setSinAlert(false);
    }

    handleSubmit(values, { updateButtonControls }, true);
    dispatch(setSubmitted(true));
    backToList();
  };

  if (loading) {
    return <Spinner size="large" />;
  }

  return (
    <div className="margin-top-6">
      {!editUsers &&
      additionalsDraftData?.requestStatusCode ===
        ADDITIONAL_REQUEST_STATUS.DRAFT ? (
        <div>
          <div className="bg-primary-lightest padding-3 margin-top-3">
            This additional request is currently in DRAFT status.
            <br />
            You will be able to view the full request details after it has been
            submitted for approval.
          </div>
          <Button
            type="button"
            variant="outline"
            label="Return to search"
            className="margin-top-3"
            onClick={() => {
              backToList();
            }}
            data-testid="return-to-search"
            leftIcon={{
              name: 'ArrowBack',
            }}
          />
        </div>
      ) : (
        <>
          <FormGenerator
            ref={formGeneratorRef}
            id="vehicle-garage-form"
            schema={additionalsGarageSchema}
            content={content}
            defaultValues={values}
            onSubmit={handleSubmit}
            onError={console.error}
            useFormProps={{
              mode: 'onBlur',
              reValidateMode: 'onBlur',
            }}
          />

          <AvailableVehiclesAdditionals />

          <AdditionalRequestActionButtons
            cancelButton={{
              label: 'Cancel request',
              disabled: false,
            }}
            saveButton={{
              onClick: handleSaveDraft,
              disabled: false,
            }}
            submitButton={{
              label: 'Next',
              leftIcon: null,
              rightIcon: { name: 'arrow_forward' },
              // onClick: handleSaveDraft,
              disabled: creatingAdditionalrequest,
            }}
            nextFormId={'vehicle-garage-form'}
            hideButtons={[
              'previous',
              'returnToSearch',
              'returnRequest',
              'fillExistVehicle',
            ]}
          />
        </>
      )}
    </div>
  );
}
